import { useCallback, useState } from 'react';

import { isUserRightToLeft } from 'js/lib/language';

type Props = {
  container: HTMLDivElement | null;
  buffer: number;
  totalImageBlockWidth: number;
};

const useCustomScroll = ({ container, buffer, totalImageBlockWidth }: Props) => {
  const [canScrollNext, setCanScrollNext] = useState(true);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // custom smooth scrolling since chrome's native smooth scrolling wasn't working as of Dec. 3 2024
  // used in pathwayCourseProgression
  const smoothScroll = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (targetScrollLeft: number, duration = 500) => {
      if (!container) {
        return;
      }

      const startScrollLeft = container.scrollLeft;
      const distance = targetScrollLeft - startScrollLeft;
      let startTime: number | null = null;

      const animateScroll = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1); // Clamps the progress value to 1

        // Update the scroll position based on the progress
        container.scrollLeft = startScrollLeft + distance * progress;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    },
    [container]
  );

  const updateScrollButtons = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    if (!container) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const left = isUserRightToLeft() ? -scrollLeft : scrollLeft;

    setCanScrollNext(left + clientWidth < scrollWidth - buffer);
    setCanScrollPrev(left > buffer);
    setScrolled(left > buffer);
  }, [container, buffer]);

  const scrollPrevious = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    if (!container) {
      return;
    }
    const { clientWidth, scrollLeft } = container;
    const fullyVisibleImages = Math.floor(clientWidth / totalImageBlockWidth);
    const scrollDistance = fullyVisibleImages * totalImageBlockWidth;
    smoothScroll(isUserRightToLeft() ? scrollLeft + scrollDistance : scrollLeft - scrollDistance);
    setTimeout(updateScrollButtons, 600);
  }, [container, totalImageBlockWidth, smoothScroll, updateScrollButtons]);

  const scrollNext = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    if (!container) {
      return;
    }
    setScrolled(true);
    const { clientWidth, scrollLeft } = container;
    const fullyVisibleImages = Math.floor(clientWidth / totalImageBlockWidth);
    const scrollDistance = fullyVisibleImages * totalImageBlockWidth;
    smoothScroll(isUserRightToLeft() ? scrollLeft - scrollDistance : scrollLeft + scrollDistance);
    setTimeout(updateScrollButtons, 600);
  }, [container, totalImageBlockWidth, smoothScroll, updateScrollButtons]);

  if (!container) {
    return {};
  }

  return { updateScrollButtons, scrollPrevious, scrollNext, canScrollNext, canScrollPrev, scrolled };
};

export default useCustomScroll;
